import React, { useEffect, useRef } from 'react';
import { XIcon, DownloadIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
interface Result {
  pathogen: string;
  type: string;
  detected: boolean;
  concentration: string;
}
interface ResultsModalProps {
  results: Result[];
  onSave: () => void;
  onClose: () => void;
}
export const ResultsModal: React.FC<ResultsModalProps> = ({
  results,
  onSave,
  onClose
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  // Calculate statistics
  const detectedResults = results.filter(r => r.detected);
  const totalDetected = detectedResults.length;
  const typeCount = detectedResults.reduce((acc, result) => {
    acc[result.type] = (acc[result.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" data-id="element-88">
      <div ref={modalRef} className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" data-id="element-89">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700" data-id="element-90">
          <h3 className="text-xl font-semibold" data-id="element-91">Detection Results</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300" aria-label="Close" data-id="element-92">
            <XIcon size={24} data-id="element-93" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6" data-id="element-94">
          {/* Summary Statistics */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 rounded-lg" data-id="element-95">
            <h4 className="font-semibold text-lg mb-3" data-id="element-96">Detection Summary</h4>
            <div className="grid grid-cols-2 gap-4" data-id="element-97">
              <div data-id="element-98">
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-99">
                  Total Pathogens Detected
                </p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-id="element-100">
                  {totalDetected}
                </p>
              </div>
              <div data-id="element-101">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-102">
                  Types Detected
                </p>
                <div className="space-y-1" data-id="element-103">
                  {Object.entries(typeCount).map(([type, count]) => <p key={type} className="text-sm" data-id="element-104">
                      <span className="font-medium" data-id="element-105">{count}</span> {type}
                    </p>)}
                </div>
              </div>
            </div>
          </div>
          {/* Results Table */}
          <div className="overflow-x-auto" data-id="element-106">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-107">
              <thead className="bg-gray-50 dark:bg-gray-750" data-id="element-108">
                <tr data-id="element-109">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-110">
                    Pathogen
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-111">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-112">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-113">
                    Concentration (mg/mL)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-114">
                {results.map((result, index) => <tr key={index} data-id="element-115">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" data-id="element-116">
                      {result.pathogen}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-117">
                      {result.type}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-118">
                      {result.detected ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" data-id="element-119">
                          <CheckCircleIcon size={14} className="mr-1" data-id="element-120" />
                          Detected
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200" data-id="element-121">
                          <XCircleIcon size={14} className="mr-1" data-id="element-122" />
                          Not Detected
                        </span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-123">
                      {result.concentration}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700" data-id="element-124">
          <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-125">
            Would you like to save these results?
          </p>
          <div className="flex gap-3" data-id="element-126">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600" data-id="element-127">
              Close
            </button>
            <button onClick={onSave} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-128">
              <DownloadIcon size={16} className="mr-2" data-id="element-129" />
              Save Results
            </button>
          </div>
        </div>
      </div>
    </div>;
};